import { useTranslations } from '@vocab/react';
import { Box, Divider, Stack, Text } from 'braid-design-system';
import { useMemo } from 'react';

import translations from 'src/pages/ConnectedPartners/.vocab';
import { TIERS } from 'src/tiers';
import type { Partner } from 'src/types';

import { PartnerAccordion } from './PartnerAccordion';

interface Props {
  data: Record<Partner['tier'], Partner[]>;
  partnerFilter: string;
  regionFilter: string;
  solutionFilter: string[];
}

export const PartnerList = ({
  data,
  partnerFilter,
  regionFilter,
  solutionFilter,
}: Props) => {
  const { t } = useTranslations(translations);

  const filteredData = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(data).map(([tier, tierPartners]) => [
          tier,
          tierPartners.filter((partner) => {
            if (partnerFilter) {
              return (
                // Name overrides all other filters
                partner.name === partnerFilter
              );
            }

            const matchesRegionFilter = // If user specifically picks a market in Asia, require partner.hasApacIntegration
              regionFilter === '' ||
              regionFilter === 'All' ||
              partner.hasApacIntegration ||
              ['AU', 'NZ'].includes(regionFilter);

            const matchesSolutionFilter = solutionFilter.every((solution) =>
              partner.configurableRelationships.some(
                ({ typeCode }) => typeCode === solution,
              ),
            );

            return matchesRegionFilter && matchesSolutionFilter;
          }),
        ]),
      ),
    [data, partnerFilter, solutionFilter, regionFilter],
  );

  const count = Object.values(filteredData).flat().length;
  const forceExpanded = count === 1;

  return (
    <>
      <Stack space="xlarge">
        <Box>
          <Stack space="xsmall">
            <Text>{t('connectedPartnersCount', { partnerCount: count })}:</Text>
            <Divider />
          </Stack>
        </Box>

        <Stack space="gutter">
          {TIERS.map(({ tier, label }) =>
            filteredData[tier]?.length > 0 ? (
              <PartnerAccordion
                key={tier}
                label={label}
                partners={filteredData[tier]}
                forceExpanded={forceExpanded}
                tier={tier}
              />
            ) : null,
          )}
        </Stack>
      </Stack>
    </>
  );
};
